import React, { useCallback, useEffect, useRef, useState } from "react";

import { AdministrationCard } from "@common/components/AdministrationCard/AdministrationCard";
import Button from "@common/components/Button/Button";
import { GridTable } from "@common/components/GridTable/GridTable";
import { useModal } from "@common/components/Modal/Modal";
import { App } from "@common/models";
import { getIntegrationTypeName, Integration, INTEGRATION_TO_HASH, IntegrationType } from "@common/models/integrations";
import { helpCenterArticles } from "@common/util/helpCenterArticles";
import identifiers from "@common/util/identifiers.json";
import { showIntercomArticle } from "@common/util/intercom";
import useWorkspaceAppsApiConsumer from "@main/consumers/useWorkspaceAppsApiConsumer";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { AsanaIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/AsanaIntegrationModal";
import { AzureIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/AzureIntegrationModal";
import { ClickupIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/ClickupIntegrationModal";
import { useRedirectConsumer } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/consumers/shared/useRedirectConsumer";
import { GithubIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/GithubIntegrationModal";
import { JiraIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/JiraIntegrationModal";
import { JiraServerIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/JiraServerIntegrationModal";
import { LinearIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/LinearIntegrationModal";
import { NotionIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/NotionIntegrationModal";
import { SlackIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/SlackIntegrationModal";
import { TrelloIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/TrelloIntegrationModal";
import { WebhookIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/WebhookIntegrationModal";
import { ZapierIntegrationModal } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/ZapierIntegrationModal";

import * as Styled from "./AppsCard.styles";
import { UserFeedbackIntegrationsRow } from "../UserFeedbackIntegrationsRow/UserFeedbackIntegrationsRow";

type Props = {
  app: App;
  disconnectIntegration: (integration: Integration) => Promise<void>;
  disabled: boolean;
  loading: boolean;
  preSelectedIntegrationType: IntegrationType | undefined;
  tooltip: { text: string };
  openIntegration?: string;
};

export function UserFeedbackIntegrationsCard({
  disconnectIntegration,
  app,
  disabled,
  loading,
  preSelectedIntegrationType,
  tooltip,
}: Props) {
  const { selectedWorkspace } = useAppSelectionContext();
  const showJiraToggles = false; // We've disabled this for now.
  // const showJiraToggles: boolean = selectedWorkspace
  //   ? new Date(selectedWorkspace.created) <= new Date("2023-04-14T00:00:00.000000")
  //   : true;

  const { integrations } = app;
  const hasJira: boolean = integrations.some((i) => i.type === IntegrationType.JIRA);

  const { fetchAllApps } = useWorkspaceAppsApiConsumer();

  const { startRedirection } = useRedirectConsumer({ workspace: selectedWorkspace, selectedAppId: app.id });

  const [selectedIntegrationType, setSelectedIntegrationType] = useState<IntegrationType | undefined>(
    preSelectedIntegrationType,
  );
  const selectedIntegration: Integration | undefined = selectedIntegrationType
    ? integrations.find(({ type }) => type === selectedIntegrationType)
    : undefined;

  const integrationHash: string | undefined = selectedIntegrationType
    ? `#${INTEGRATION_TO_HASH[selectedIntegrationType]}`
    : undefined;

  const {
    Modal: IntegrationModalComponent,
    setActive: setIntegrationModalActive,
    modalProps: integrationModalProps,
  } = useModal({
    size: "full",
    hash: integrationHash,
    autoOpen: false,
  });

  const {
    Modal: DisconnectIntegrationModal,
    setActive: setDisconnectModalActive,
    modalProps: disconnectModalProps,
  } = useModal({ size: "narrow" });

  const handleConnect = useCallback(
    (integrationType: IntegrationType) => {
      const shouldShowModal: boolean =
        integrationType === IntegrationType.SLACK ||
        integrationType === IntegrationType.ZAPIER ||
        integrationType === IntegrationType.AZURE_DEVOPS ||
        integrationType === IntegrationType.JIRA_SERVER ||
        integrationType === IntegrationType.WEBHOOK;
      if (shouldShowModal) {
        setSelectedIntegrationType(integrationType);
        setIntegrationModalActive(true);
      } else {
        startRedirection(integrationType);
      }
    },
    [startRedirection, setIntegrationModalActive],
  );

  const handleDisconnect = useCallback(
    async (selectedIntegration: Integration | undefined) => {
      if (!selectedIntegration) return;

      await disconnectIntegration(selectedIntegration);

      setDisconnectModalActive(false);
      setSelectedIntegrationType(undefined);
    },
    [disconnectIntegration, setDisconnectModalActive],
  );

  const didHandlePreselect = useRef(false);

  useEffect(() => {
    if (!preSelectedIntegrationType || didHandlePreselect.current) return;

    didHandlePreselect.current = true;

    if (!selectedIntegration) {
      handleConnect(preSelectedIntegrationType);
    } else {
      setSelectedIntegrationType(preSelectedIntegrationType);
      setIntegrationModalActive(true);
    }
  }, [handleConnect, preSelectedIntegrationType, selectedIntegration, setIntegrationModalActive]);

  const handleIntegrationFinish = useCallback(() => {
    setSelectedIntegrationType(undefined);
    setIntegrationModalActive(false);
  }, [setIntegrationModalActive]);

  const renderIntegrationsModal = () => {
    if (!selectedIntegrationType || !selectedWorkspace) return null;

    switch (selectedIntegrationType) {
      case IntegrationType.GITHUB:
        return (
          <GithubIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.github}
            selectedAppId={app.id}
            workspace={selectedWorkspace}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.ASANA:
        return (
          <AsanaIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.asana}
            selectedAppId={app.id}
            workspace={selectedWorkspace}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.CLICKUP:
        return (
          <ClickupIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.clickup}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.TRELLO:
        return (
          <TrelloIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.trello}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.SLACK:
        return (
          <SlackIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.slack}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.ZAPIER:
        return (
          <ZapierIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.zapier}
            selectedAppId={app.id}
            workspace={selectedWorkspace}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.AZURE_DEVOPS:
        return (
          <AzureIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.azure}
            selectedAppId={app.id}
            workspace={selectedWorkspace}
            onFinish={handleIntegrationFinish}
            onAuthSuccess={async () => {
              try {
                await fetchAllApps(selectedWorkspace.id);
                setSelectedIntegrationType(IntegrationType.AZURE_DEVOPS);
              } catch (error) {}
            }}
          />
        );
      case IntegrationType.JIRA_SERVER:
        return (
          <JiraServerIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.jira}
            selectedAppId={app.id}
            workspace={selectedWorkspace}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.JIRA_CLOUD:
      case IntegrationType.JIRA:
        return (
          <JiraIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.jira}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
            showToggles={showJiraToggles ?? false}
          />
        );
      case IntegrationType.LINEAR:
        return (
          <LinearIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.linear}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
            workspace={selectedWorkspace}
          />
        );
      case IntegrationType.NOTION:
        return (
          <NotionIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.notion}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
          />
        );
      case IntegrationType.WEBHOOK:
        return (
          <WebhookIntegrationModal
            Modal={IntegrationModalComponent}
            modalProps={integrationModalProps}
            integration={app.webhook}
            selectedAppId={app.id}
            onFinish={handleIntegrationFinish}
          />
        );
      default:
        return null;
    }
  };

  const renderDisconnectModal = () => {
    if (!selectedIntegrationType || !selectedWorkspace) return null;

    return (
      <DisconnectIntegrationModal
        {...disconnectModalProps}
        heading={`Disconnect ${getIntegrationTypeName(selectedIntegrationType)}?`}
        subtitle={`This will disable ${getIntegrationTypeName(
          selectedIntegrationType,
        )} and return it to its default settings.`}
        buttonElement={
          <Button
            onClick={() => handleDisconnect(selectedIntegration)}
            size="small"
            color="red"
            disabled={loading}
            loadingText={loading && "Disconnecting..."}
          >
            {"Disconnect"}
          </Button>
        }
      />
    );
  };

  return (
    <AdministrationCard
      title="Feedback integrations"
      subtitle="Choose where to forward feedback tickets."
      testId={identifiers["integrations.header"]}
    >
      {
        <GridTable gridTemplateColumns="repeat(2, minmax(10rem, 18rem)) repeat(1, auto)">
          {Object.values(IntegrationType)
            .sort((a, b) => a.localeCompare(b))
            .filter(
              (integrationType) =>
                integrationType !== IntegrationType.SENTRY &&
                (hasJira
                  ? integrationType !== IntegrationType.JIRA_CLOUD && integrationType !== IntegrationType.JIRA_SERVER
                  : integrationType !== IntegrationType.JIRA),
            )
            .map((integrationType) => {
              const integration = integrations.find((i) => i.type === integrationType) || null;
              return (
                <UserFeedbackIntegrationsRow
                  key={integrationType}
                  integration={integration}
                  type={integrationType}
                  onConnect={handleConnect}
                  onDisconnect={() => {
                    setSelectedIntegrationType(integrationType);
                    setDisconnectModalActive(true);
                  }}
                  onSettingsSelect={() => {
                    setSelectedIntegrationType(integrationType);
                    setIntegrationModalActive(true);
                  }}
                  tooltip={tooltip.text}
                  disabled={disabled}
                />
              );
            })}
        </GridTable>
      }
      {getHelpers()}
      {integrationModalProps.active ? renderIntegrationsModal() : null}
      {disconnectModalProps.active ? renderDisconnectModal() : null}
    </AdministrationCard>
  );
}

const getHelpers = () => {
  return (
    <>
      <Styled.HelperText $bottomMargin>
        {"In case you ever get stuck, here’s a great Help Center article: "}
        <a onClick={() => showIntercomArticle(helpCenterArticles.integrationsTroubleshooting)}>
          Troubleshooting integrations
        </a>
      </Styled.HelperText>
    </>
  );
};
